import type {FirebaseOptions} from 'firebase/app';

export type PublicOperatorConfig = {
  displayName: string;
  privacyContactUrl: string;
};

export type PursuitDeckRuntimeConfig = FirebaseOptions & {
  appName?: string;
  commitSha: string;
  operator: PublicOperatorConfig;
};

const record = (value: unknown, field: string): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`invalid_runtime_config:${field}`);
  }
  return value as Record<string, unknown>;
};

const requiredString = (value: unknown, field: string, maxLength = 500): string => {
  if (typeof value !== 'string') throw new Error(`invalid_runtime_config:${field}`);
  const normalized = value.trim();
  if (!normalized || normalized.length > maxLength || /[\u0000-\u001f\u007f]/.test(normalized)) {
    throw new Error(`invalid_runtime_config:${field}`);
  }
  return normalized;
};

export const isPrivacyContactUrl = (value: string): boolean => {
  if (value.length > 2_048 || /[\u0000-\u001f\u007f]/.test(value)) return false;
  if (value.startsWith('mailto:')) {
    const address = value.slice('mailto:'.length);
    const local = address.split('@', 1)[0] ?? '';
    return address.length <= 320
      && !/[?%#]/.test(address)
      && !local.startsWith('.')
      && !local.endsWith('.')
      && !local.includes('..')
      && /^[A-Za-z0-9.!#$&'*+/=?^_{}|~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/.test(address);
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:'
      && Boolean(parsed.hostname)
      && !parsed.username
      && !parsed.password
      && (!parsed.port || parsed.port === '443');
  } catch {
    return false;
  }
};

export const parseRuntimeConfig = (value: unknown): PursuitDeckRuntimeConfig => {
  const input = record(value, 'root');
  const operator = record(input.operator, 'operator');
  const privacyContactUrl = requiredString(
    operator.privacyContactUrl,
    'operator.privacyContactUrl',
    2_048,
  );
  if (!isPrivacyContactUrl(privacyContactUrl)) {
    throw new Error('invalid_runtime_config:operator.privacyContactUrl');
  }
  const appName = input.appName === undefined
    ? undefined
    : requiredString(input.appName, 'appName', 100);
  const commitSha = requiredString(input.commitSha, 'commitSha', 40);
  if (!/^[0-9a-f]{40}$/.test(commitSha)) {
    throw new Error('invalid_runtime_config:commitSha');
  }
  return {
    apiKey: requiredString(input.apiKey, 'apiKey'),
    authDomain: requiredString(input.authDomain, 'authDomain'),
    projectId: requiredString(input.projectId, 'projectId'),
    appId: requiredString(input.appId, 'appId'),
    commitSha,
    ...(appName ? {appName} : {}),
    operator: {
      displayName: requiredString(operator.displayName, 'operator.displayName', 100),
      privacyContactUrl,
    },
  };
};

export const firebaseOptionsFromRuntimeConfig = ({
  apiKey,
  authDomain,
  projectId,
  appId,
}: PursuitDeckRuntimeConfig): FirebaseOptions => ({
  apiKey,
  authDomain,
  projectId,
  appId,
});
